import { useAuth, useUser } from '@clerk/clerk-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';

import { api } from '@core/api/client';
import { qk } from '@core/api/queryKeys';
import type {
  CurrentUserResponse,
  OrganizationSummary,
  OrganizationUsage,
  UserRole,
} from '@core/types/api';

import { roleSatisfies } from './roles';

/** A membership as the switcher renders it, with "is default" already resolved. */
export interface Membership {
  organizationId: string;
  name: string;
  slug: string;
  role: UserRole;
  isDefault: boolean;
}

export interface CurrentUser {
  isLoading: boolean;
  isSignedIn: boolean;
  /** The membership id in the current organization. */
  id?: string;
  clerkId?: string;
  organizationId?: string;
  organizationName?: string;
  /** Null when the caller has no organization at all; undefined while loading. */
  organization?: OrganizationSummary | null;
  usage: OrganizationUsage | null;
  platformAdmin: boolean;
  memberships: Membership[];
  defaultOrganizationId: string | null;
  name: string;
  email?: string;
  imageUrl?: string;
  /** Undefined with no organization: a platform admin outside every tenant has no role. */
  role?: UserRole;
  permissions: string[];
  /** Hierarchical: an OWNER satisfies `can('DISPATCHER')`. Always false with no role. */
  can: (required: UserRole) => boolean;
  hasPermission: (permission: string) => boolean;
  error: unknown;
  /** Re-runs the bootstrap call. */
  retry: () => void;
}

const ME = '/api/v1/users/me';

export const currentUserService = {
  me: async (signal?: AbortSignal) => (await api.get<CurrentUserResponse>(ME, { signal })).data,

  /** Null clears the default, so the oldest membership is used. */
  setDefaultOrganization: async (organizationId: string | null) =>
    (await api.put<CurrentUserResponse>(`${ME}/default-organization`, { organizationId })).data,
};

/**
 * Merges Clerk's profile with our own role and organization.
 *
 * The split matters: Clerk owns identity (name, avatar, email), this application owns
 * authorization. A client that read its role from a Clerk claim would be trusting a
 * value the user's own dashboard can influence.
 *
 * `/users/me` is also where the backend provisions the local user row on first sign-in,
 * so the first call after sign-up can be slower than later ones.
 */
export function useCurrentUser(): CurrentUser {
  const { isLoaded, isSignedIn } = useAuth();
  const { user } = useUser();

  const me = useQuery({
    queryKey: qk.me(),
    queryFn: ({ signal }) => currentUserService.me(signal),
    enabled: isLoaded && Boolean(isSignedIn),
    staleTime: 5 * 60_000,
    // A failed /me leaves the app unusable, so surface it quickly rather than retrying
    // through three backoffs while the user stares at a splash.
    retry: 1,
    // Do not re-ask on every focus change. The realistic failures here -- a suspended
    // organization, a missing Clerk claim -- are stable, and refetching turns a single
    // clear error into a stream of identical 403s every time the window is touched.
    refetchOnWindowFocus: false,
  });

  return useMemo(() => {
    const data = me.data;
    const role = data?.role ?? undefined;
    const permissions = data?.permissions ?? [];
    const defaultOrganizationId = data?.defaultOrganizationId ?? null;

    return {
      isLoading: !isLoaded || (Boolean(isSignedIn) && me.isPending),
      isSignedIn: Boolean(isSignedIn),
      id: data?.id ?? undefined,
      clerkId: user?.id,
      organizationId: data?.organization?.id,
      organizationName: data?.organization?.name,
      // `?? null`, not a plain read: the server omits null fields, so "no organization"
      // arrives as an absent key. Undefined is reserved for "not loaded yet".
      organization: data ? (data.organization ?? null) : undefined,
      usage: data?.usage ?? null,
      platformAdmin: data?.platformAdmin ?? false,
      memberships: (data?.memberships ?? []).map((membership) => ({
        organizationId: membership.organizationId,
        name: membership.name,
        slug: membership.slug,
        role: membership.role,
        // Derived rather than read from the flag: see MembershipResponse.isDefault.
        isDefault: membership.organizationId === defaultOrganizationId,
      })),
      defaultOrganizationId,
      name: user?.fullName ?? data?.displayName ?? '',
      email: user?.primaryEmailAddress?.emailAddress ?? data?.email ?? undefined,
      imageUrl: user?.imageUrl,
      role,
      permissions,
      can: (required: UserRole) => roleSatisfies(role, required),
      hasPermission: (permission: string) => permissions.includes(permission),
      error: me.error,
      // Exposed so the bootstrap gate can offer a retry. A failed /me is not always a
      // decision about this user -- it is just as often a backend that was still starting.
      retry: () => void me.refetch(),
    };
  }, [isLoaded, isSignedIn, user, me.data, me.isPending, me.error, me.refetch]);
}

/**
 * Makes an organization the one a new tab (or a new sign-in) lands in.
 *
 * <p>Does not switch THIS tab: choosing a default and moving are different intentions,
 * and starring an organization should not yank the screen out from under the user.
 */
export function useSetDefaultOrganization() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (organizationId: string | null) =>
      currentUserService.setDefaultOrganization(organizationId),
    onSuccess: (me) => queryClient.setQueryData(qk.me(), me),
  });
}
